import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, BookOpen, Layers, Users, ShoppingCart, GraduationCap,
  BarChart3, FileText, Bell, Settings, ArrowLeft, Menu, X,
  Sun, Moon, LogOut, ChevronDown, StickyNote, BookMarked, FileDown, Gift, Database, Video, PlayCircle, ClipboardList
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_1791f9d8-ffce-4632-931c-e01b5c5c94c3/artifacts/sbeyyf6q_1000003487.png";

const navGroups = [
  {
    label: "Main",
    items: [
      { label: "Dashboard", icon: LayoutDashboard, path: "/admin" },
      { label: "Analytics", icon: BarChart3, path: "/admin/analytics" },
    ]
  },
  {
    label: "Content",
    items: [
      { label: "Courses", icon: BookOpen, path: "/admin/courses" },
      { label: "Free Classes", icon: PlayCircle, path: "/admin/free-classes" },
      { label: "Mock Tests", icon: ClipboardList, path: "/admin/mock-tests" },
      { label: "Live", icon: Video, path: "/admin/live" },
      { label: "Categories", icon: Layers, path: "/admin/categories" },
      { label: "PDF Notes", icon: StickyNote, path: "/admin/notes" },
      { label: "Content", icon: FileText, path: "/admin/content" },
      { label: "Banners", icon: FileText, path: "/admin/banners" },
    ]
  },
  {
    label: "People",
    items: [
      { label: "Users", icon: Users, path: "/admin/users" },
      { label: "Instructors", icon: GraduationCap, path: "/admin/instructors" },
    ]
  },
  {
    label: "Commerce",
    items: [
      { label: "Orders", icon: ShoppingCart, path: "/admin/orders" },
      { label: "Printed Books", icon: BookMarked, path: "/admin/printed-books" },
      { label: "E-Books", icon: FileDown, path: "/admin/ebooks" },
      { label: "Free E-Books", icon: Gift, path: "/admin/free-ebooks" },
    ]
  },
  {
    label: "System",
    items: [
      { label: "Notifications", icon: Bell, path: "/admin/notifications" },
      { label: "Data Migration", icon: Database, path: "/admin/data-migration" },
      { label: "Settings", icon: Settings, path: "/admin/settings" },
    ]
  }
];

export default function AdminLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("admin_dark") === "true");
  const [collapsedGroups, setCollapsedGroups] = useState({});

  useEffect(() => {
    localStorage.setItem("admin_dark", darkMode);
  }, [darkMode]);

  const toggleGroup = (label) => {
    setCollapsedGroups(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const bg = darkMode ? "bg-[#0B1120]" : "bg-slate-50";
  const sidebarBg = darkMode ? "bg-[#111827]" : "bg-white";
  const sidebarText = darkMode ? "text-slate-300" : "text-slate-600";
  const sidebarActive = darkMode ? "bg-[#0284C7]/20 text-[#38BDF8]" : "bg-[#0284C7]/10 text-[#0284C7]";
  const sidebarHover = darkMode ? "hover:bg-white/5" : "hover:bg-slate-50";
  const headerBg = darkMode ? "bg-[#111827] border-[#1E293B]" : "bg-white border-slate-200";
  const headerText = darkMode ? "text-white" : "text-slate-900";
  const groupLabel = darkMode ? "text-slate-500" : "text-slate-400";
  const contentBg = darkMode ? "bg-[#0B1120]" : "bg-slate-50";
  const cardBg = darkMode ? "bg-[#111827]" : "bg-white";

  return (
    <div className={`min-h-screen ${bg}`} data-testid="admin-layout" data-theme={darkMode ? "dark" : "light"}>
      {/* Top Header */}
      <header className={`sticky top-0 z-50 ${headerBg} border-b h-14 flex items-center justify-between px-4`}>
        <div className="flex items-center gap-3">
          <button
            data-testid="admin-menu-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className={`lg:hidden ${headerText}`}
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <img src={LOGO_URL} alt="Logo" className="w-7 h-7 object-contain" />
          <span className={`font-bold text-sm ${headerText}`} style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Admin Panel
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="dark-mode-toggle"
            onClick={() => setDarkMode(!darkMode)}
            className={`p-2 ${darkMode ? "text-yellow-400" : "text-slate-500"} transition-none`}
            title={darkMode ? "Light Mode" : "Dark Mode"}
          >
            {darkMode ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button
            data-testid="admin-back-to-app"
            onClick={() => navigate("/")}
            className={`flex items-center gap-1 text-xs font-medium ${darkMode ? "text-slate-400 hover:text-white" : "text-slate-500 hover:text-slate-900"} transition-none`}
          >
            <ArrowLeft size={14} />
            <span className="hidden sm:inline">Back to App</span>
          </button>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          data-testid="admin-sidebar"
          className={`fixed lg:sticky top-14 left-0 bottom-0 w-60 ${sidebarBg} z-40 flex flex-col overflow-y-auto border-r ${darkMode ? "border-[#1E293B]" : "border-slate-200"} ${
            sidebarOpen ? "block" : "hidden lg:flex"
          }`}
          style={{ height: "calc(100vh - 3.5rem)" }}
        >
          {/* User info */}
          <div className={`px-4 py-4 border-b ${darkMode ? "border-[#1E293B]" : "border-slate-100"}`}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#0284C7] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                {(user?.name || "A").charAt(0)}
              </div>
              <div className="min-w-0">
                <p className={`text-sm font-bold truncate ${darkMode ? "text-white" : "text-slate-900"}`}>{user?.name || "Admin"}</p>
                <p className={`text-[10px] truncate ${darkMode ? "text-slate-400" : "text-slate-500"}`}>{user?.email}</p>
              </div>
            </div>
          </div>

          {/* Navigation Groups */}
          <nav className="flex-1 py-2 px-2">
            {navGroups.map((group) => (
              <div key={group.label} className="mb-1">
                <button
                  onClick={() => toggleGroup(group.label)}
                  className={`w-full flex items-center justify-between px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] ${groupLabel}`}
                >
                  {group.label}
                  <ChevronDown size={12} className={collapsedGroups[group.label] ? "rotate-180" : ""} />
                </button>
                {!collapsedGroups[group.label] && group.items.map((item) => {
                  const Icon = item.icon;
                  const active = location.pathname === item.path;
                  return (
                    <button
                      key={item.path}
                      data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                      onClick={() => { navigate(item.path); setSidebarOpen(false); }}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-left text-[13px] font-medium transition-none mb-0.5 ${
                        active ? sidebarActive : `${sidebarText} ${sidebarHover}`
                      }`}
                    >
                      <Icon size={16} strokeWidth={active ? 2.5 : 1.5} />
                      {item.label}
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>

          {/* Logout */}
          <button
            data-testid="admin-logout-btn"
            onClick={handleLogout}
            className={`flex items-center gap-3 px-5 py-3 text-sm font-medium border-t transition-none ${
              darkMode ? "border-[#1E293B] text-red-400 hover:bg-red-900/20" : "border-slate-100 text-red-500 hover:bg-red-50"
            }`}
          >
            <LogOut size={16} />
            Logout
          </button>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
        )}

        {/* Main content */}
        <main className={`flex-1 min-h-[calc(100vh-3.5rem)] p-4 lg:p-6 ${contentBg}`}>
          <div className="admin-content" data-dark={darkMode}>
            {/* Pass dark mode context via CSS custom properties */}
            <style>{`
              .admin-content[data-dark="true"] .admin-card { background: #111827; color: #E2E8F0; border-color: #1E293B; }
              .admin-content[data-dark="true"] .admin-text { color: #E2E8F0; }
              .admin-content[data-dark="true"] .admin-text-secondary { color: #94A3B8; }
              .admin-content[data-dark="true"] .admin-input { background: #1E293B; color: #E2E8F0; border-color: #334155; }
              .admin-content[data-dark="true"] .admin-table-row { border-color: #1E293B; }
              .admin-content[data-dark="true"] .admin-table-row:nth-child(even) { background: #0F172A; }
              .admin-content[data-dark="false"] .admin-card { background: #FFFFFF; color: #0F172A; border-color: #E2E8F0; }
              .admin-content[data-dark="false"] .admin-text { color: #0F172A; }
              .admin-content[data-dark="false"] .admin-text-secondary { color: #64748B; }
              .admin-content[data-dark="false"] .admin-input { background: #F8FAFC; color: #0F172A; border-color: #CBD5E1; }
              .admin-content[data-dark="false"] .admin-table-row { border-color: #F1F5F9; }
            `}</style>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
